import { EditFilled } from "@ant-design/icons"
import { cloneDeep, first, indexOf, findIndex } from "lodash"
import config from "react-global-configuration"

import { CONFIGURATION_SUCCESS } from "client/config/constant"
import { RESET_STORE } from "client/services/constants"
import { cdn } from "core/cdn"
import { INSERT_RELATIVE_PAGE, MODIFY_RELATIVE_PAGE } from "core/constants"
import { MAIN_PATIENT_ID } from "containers/Upload/services/constants"
import { isBirthdateBetween } from "lib/form"
import { languages } from "locales/languages"
import { age, canAccessToPreventionReport } from "lib/utils"
import { Customer, Doctor, Relative } from "types/entity"
import { CustomerType, QuestionLanguages } from "types/props"
import { Action, Message } from "types/redux"
import { ConfigurationStore, SurveyStore } from "types/store"

import {
  NUMBER_OF_SLOTS_AFTER_NOW,
  NUMBER_OF_SLOTS_BEFORE_NOW,
  PATH,
  PATH_DEEP,
  QUESTIONS,
  selected_documents,
  surveyLanguages,
} from "../models"
import { ANAMNESIS_QUESTION_IDS, SurveySnapshot } from "./surveyStorage"
import { LatestAnamnesisCache, mergePrefilledAnswer } from "./latestAnamnesis"
import {
  markPrefilled,
  markTouched,
  restorePrefillState,
  settleQuestion,
  withoutPendingAnswers,
  withoutPendingDetails,
} from "./prefillProvenance"
import { ACTION, Answer, Question, QUESTION_TYPE, Reply, Value } from "../types"
import {
  get_complete_path,
  getSlotsReplies,
  getUsefulData,
  is_deep_path,
  recursive_get,
  replace_question,
  update_answers,
  update_path,
  valueEquals,
} from "../utils"
import { getDermatoSubReasonByValue } from "../dermato_models"
import {
  ADD_DOCUMENTS_TO_QUESTION,
  CLEAR_RESTORED_DOC_SELECTION,
  CREATE_QUESTION,
  INIT_SURVEY,
  JUMP_TO_QUESTION,
  NEXT_QUESTION,
  PREFILL_ANAMNESIS,
  PREVIOUS_QUESTION,
  QUESTION_DOCTORS,
  QUESTION_PATIENT,
  QUESTION_RELATIVES,
  QUESTION_SLOTS,
  HYDRATE_SURVEY,
  REMOVE_QUESTION,
  REMOVE_FROM_PATH,
  REPLACE_QUESTIONS,
  RESET_ANAMNESIS,
  RESET_SURVEY_QUESTION_REPLIES,
  RESET_SURVEY_REPLY,
  RESET_SURVEY_STORE,
  SET_CUSTOMER_TYPE,
  SET_DOCUMENT_NAVIGATION_LOADING,
  SET_LATEST_ANAMNESIS,
  SET_QUESTION,
  SUBMIT_SURVEY_ERROR,
  SUBMIT_SURVEY_REQUEST,
  SUBMIT_SURVEY_SUCCESS,
  UNSET_QUESTION,
} from "./constants"

/*
 * function set(), unset(), next(), previous(), cancel(), finish(), create(), get(),
 * create question regarding job side [slots(), relatives(), doctors()]
 *
 *
 */
const MANAGER: Partial<SurveyStore> = {
  questions: QUESTIONS,
  current: undefined,
  path: undefined,
  status: "on_going",
  answers: [],
  documentNavigationLoading: false,
}

/*
 * init question regarding the path
 */

const reset = (manager: Partial<SurveyStore>): Partial<SurveyStore> => {
  return init({
    // details screens inserted by an unvalidated prefill go with it
    ...withoutPendingDetails(manager, manager.prefill),
    answers: [],
    documentNavigationLoading: false,
    latestAnamnesis: undefined,
    prefill: undefined,
  })
}
const path = (questions: Question[]): string[] => {
  const parentIds: string[] = (questions as Question[]).map((question) => {
    return question.id
  })
  return parentIds
}

/**
 * Ajoute une question au path à la position spécifiée
 * @param currentPath - Le path actuel
 * @param questionId - L'ID de la question à ajouter
 * @param options - Position before ou after une question cible
 * @returns Le nouveau path avec la question ajoutée
 */
const addToPath = (
  currentPath: string[],
  questionId: string,
  options?: { before?: PATH; after?: PATH },
): string[] => {
  if (currentPath.includes(questionId)) {
    return currentPath
  }
  if (!options) {
    // Pas d'options, on ajoute à la fin
    return [...currentPath, questionId]
  }

  const targetId = options.after || options.before
  const targetIndex = indexOf(currentPath, targetId)

  if (targetIndex === -1) {
    // Si la question cible n'existe pas dans le path, on ajoute à la fin
    return [...currentPath, questionId]
  }

  // Insère au bon endroit selon before ou after
  const insertIndex = options.after ? targetIndex + 1 : targetIndex
  return [
    ...currentPath.slice(0, insertIndex),
    questionId,
    ...currentPath.slice(insertIndex),
  ]
}

const init = (manager = MANAGER): Partial<SurveyStore> => {
  const parentIds: string[] = (manager.questions as Question[]).map(
    (question) => {
      return question.id
    },
  )
  const current = first(manager.questions)
  return {
    ...manager,
    status: "on_going",
    path: parentIds,
    current,
  } as SurveyStore
}

const reducer = (
  state = init(MANAGER),
  action: Action<
    | Message
    | Doctor[]
    | Relative[]
    | Customer
    | Question
    | ConfigurationStore
    | { replies: Reply[]; id?: string }
    | Reply[]
    | SurveySnapshot
    | LatestAnamnesisCache
    | Answer
    | string
    | boolean
    | { id: PATH }
    | { replies: Reply[]; id?: string }
    | {
        question: Question
        options?: { after?: PATH; before?: PATH }
      }
  >,
): Partial<SurveyStore> => {
  const _path: string[] = state.path || ["unknow-current"]
  const _questions: Question[] = state.questions as Question[]
  const _answers: Answer[] = state.answers || []
  const _current: Question | Partial<Question> = state.current || {
    id: "unknow-current",
    subquestion: undefined,
  }
  
  const payload = action.payload

  const configuration = (questionType?: string) => {
    const hasSelectedDoctorType = _answers.some(
      (answer) => answer.questionId === PATH.doctor_type,
    )
    if (hasSelectedDoctorType) {
      // Survey already branched into a speciality pathway (dermato/checkup/prevention).
      // A config refetch (e.g. triggered by SET_ADMIN after a mid-survey billing update)
      // must not reset the question set back to the generic QUESTIONS array.
      return state
    }
    return init({
      ...state,
      questions: QUESTIONS,
    })
  }

  const customer = (customer: CustomerType) => {
    const genderedQuestion = (
      oldQuestion: Question & { questions?: Question[] },
    ) => {
      let question = { ...oldQuestion }
      const dictionary: QuestionLanguages = surveyLanguages.find(
        ({ id }) => id === question.id,
      ) as QuestionLanguages
      if (!dictionary) {
        return {
          ...question,
          questions: oldQuestion.questions
            ? oldQuestion.questions.map((q) => genderedQuestion(q))
            : null,
        }
      }

      let questionLanguages = null

      if (customer === "relative_male") {
        questionLanguages = dictionary?.relative?.male
      } else if (customer === "relative_female") {
        questionLanguages = dictionary?.relative?.female
      } else {
        questionLanguages = dictionary.patient
      }

      const title = (questionLanguages?.title ?? question.title) as string

      const titleDefaultReply = (questionLanguages?.defaultReply ??
        question.defaultReply?.title) as string

      const defaultReply = question.defaultReply && {
        ...question.defaultReply,
        title: titleDefaultReply,
      }

      const replies = questionLanguages?.replies
        ? question.replies.map((reply, i) => ({
            ...reply,
            title: questionLanguages!.replies![i] ?? reply.title,
          }))
        : question.replies

      return {
        ...question,
        questions: oldQuestion.questions
          ? oldQuestion.questions.map((q) => genderedQuestion(q))
          : null,
        defaultReply,
        title,
        replies,
      } as Question
    }
    const new_questions = _questions.map(genderedQuestion)
    return { ...state, questions: new_questions }
  }
  /*
   * get question from pathid
   */
  const get = (path_id: string): Question | undefined => {
    const complete_path = get_complete_path(path_id)
    const question: Question | undefined = _questions.find(({ id }) =>
      complete_path.includes(id),
    )
    if (complete_path.length === 1) return question
    return question ? recursive_get(question, complete_path) : undefined
  }

  /*
   * init question regarding the path
   */
  const set = (replies: Reply[], currentId: string): Partial<SurveyStore> => {
    let questions = _questions
    const question = get(currentId)
    let currentForPath = (question ?? _current) as Question

    if (currentId === PATH.reason) {
      const subQ = getDermatoSubReasonByValue(replies[0]?.value as string)
      if (subQ) {
        questions = questions.map((q) =>
          q.id === PATH.reason ? { ...q, subquestion: subQ } : q,
        )
        currentForPath = { ...(question ?? _current), subquestion: subQ } as Question
      }
    }

    let answers: Answer[] = cloneDeep<Answer[]>(_answers)
    let freshValues: Value[] = []
    if (question?.ui?.view === "questions_line") {
      // Lorsque la question est de type questions_line, on récupère les valeurs des réponses
      // {title : illness, value : true}
      freshValues = replies.map((r) => r)
      answers = answers.map((a) => {
        if (a.questionId === currentId) {
          return {
            ...a,
            value: a.value.filter(
              (v) =>
                !freshValues
                  .map((f) => (f as any).title)
                  .includes((v as any).title),
            ),
          }
        }
        return a
      })
    } else {
      freshValues = replies.map(({ value }) => value)
    }

    answers = update_answers(answers, {
      questionId: currentId,
      value: freshValues,
      questionTitle: question?.title,
      label: question?.label,
    })

    return {
      ...state,
      questions,
      answers,
      path: update_path(
        replies as Reply[],
        _path as string[],
        currentForPath,
      ),
    } as SurveyStore
  }

  /*
   * Unset question regarding the path
   */
  const unset = (
    unReplies: Reply[] | undefined,
    currentId: string,
  ): Partial<SurveyStore> => {
    if (!unReplies) {
      const updatedAnswers = _answers.map((answer: Answer) => {
        if (currentId === answer.questionId)
          return {
            ...answer,
            value: [],
          }
        else return answer
      })
      return { ...state, answers: updatedAnswers }
    }

    const question = get(currentId)
    const toRemove: Value[] = unReplies.map((r) =>
      question?.ui?.view === "questions_line" ? r : r.value,
    )

    const answers: Answer[] = cloneDeep<Answer[]>(_answers).map(
      ({ questionId, value }) => {
        if (questionId === currentId) {
          return {
            questionId,
            value:
              value?.filter(
                (v) => !toRemove.some((valueToRemove) => valueEquals(valueToRemove, v)),
              ) || [],
          }
        } else return { questionId, value }
      },
    )

    const path = cloneDeep<string[]>(_path)

    unReplies.forEach((reply) => {
      if (reply.action === ACTION.subquestion) {
        const subquestionId =
          getDermatoSubReasonByValue(reply.value as string)?.id ??
          _current?.subquestion?.id
        if (subquestionId && path.includes(subquestionId)) {
          path.splice(path.indexOf(subquestionId), 1)
        }
      }
    })

    return { ...state, answers, path }
  }

  /*
   * Next question regarding the path
   */
  const next = (): Partial<SurveyStore> => {
    const indexOfCurrent = _path.indexOf(_current.id || "")
    const next: string | undefined = _path[indexOfCurrent + 1]
    const current = next ? get(next) : undefined
    if (current) return { ...state, current }
    return finish()
  }

  /*
   * Retourne la question d'avant
   */
  const previous = (): Partial<SurveyStore> => {
    if (state.status === "finish") {
      return { ...state, success: false, status: "on_going" }
    }

    const indexOfCurrent = _path.indexOf(_current.id || "")
    
    const newPath = _path.filter(
        (p) => p !== _current.id || !_current.id.includes(PATH_DEEP),
    )

    let newAnswers = _answers.filter(
      (answer) => _current.id !== answer.questionId,
    )

    let previousIndex: string | undefined = _path[indexOfCurrent - 1]
    let newCurrent = previousIndex ? get(previousIndex) : undefined

    let previousPathIndex = indexOfCurrent - 1
    while (
      newCurrent &&
      (newCurrent.type === QUESTION_TYPE.loader ||
        newCurrent.type === QUESTION_TYPE.redirect)
    ) {
      previousPathIndex -= 1
      previousIndex = _path[previousPathIndex]
      newCurrent = previousIndex ? get(previousIndex) : undefined
      if (newCurrent) {
        newAnswers = _answers.filter(
          (answer) => newCurrent?.id !== answer.questionId,
        )
      }
    }
    if (newCurrent)
      return {
        ...state,
        current: newCurrent,
        path: newPath,
        answers: newAnswers,
      }
    return cancel()
  }

  const relativeReply = (r: Relative): Reply => {
    const hasValidBirthdate = isBirthdateBetween(
      r.birthdate,
      config.get("validator.age.minimum"),
      config.get("validator.age.maximum"),
    )
    return {
      imgUrl: cdn("/images/relatives_picto.svg"),
      title: `${r.firstname} ${r.lastname}`,
      value: hasValidBirthdate ? r.id : "relative_too_young",
      action: hasValidBirthdate ? ACTION.default : ACTION.inability,
      preventionReportAllowed: canAccessToPreventionReport(age(r.birthdate)),
      additionalActions: [
        {
          value: `${MODIFY_RELATIVE_PAGE}/${r.id}`,
          title: "Modifier le proche",
          icon: <EditFilled />,
          action: ACTION.redirect,
          ui: { view: "icon" },
        },
      ],
    }
  }

  const addRelativeReply: Reply = {
    imgUrl: cdn("/images/plus_picto.svg"),
    title: languages.addARelative,
    value: INSERT_RELATIVE_PAGE,
    action: ACTION.redirect,
    ui: { type: "ghost" },
  }

  /*
   * Add the relatives to the "who is it for?" question
   */
  const relatives = ({
    relatives,
  }: {
    relatives: Relative[]
  }): Partial<SurveyStore> => {
    const patientQuestion: Question = get(PATH.patient) as Question
    const patientReply = patientQuestion.replies.find(
      ({ value }) => value === MAIN_PATIENT_ID,
    )

    patientQuestion.replies = [
      ...(patientReply ? [patientReply] : []),
      ...relatives.map(relativeReply),
      addRelativeReply,
    ]
    return create({ question: patientQuestion })
  }
  /*
   * Créer la question docteur
   */
  const doctors = ({
    doctors,
  }: {
    doctors: Doctor[]
  }): Partial<SurveyStore> => {
    const doctorQuestion: Question = get(PATH.doctor) as Question

    doctorQuestion.replies = doctors.map((doctor) => {
      return {
        value: doctor.id,
        title: `Dr. ${doctor.lastname}`,
        action: ACTION.subquestion,
      }
    })
    return create({ question: doctorQuestion })
  }
  /*
   * Créer la question parent
   */
  const patient = ({
    patient,
  }: {
    patient: Customer
  }): Partial<SurveyStore> => {
    const patientQuestion: Question = get(PATH.patient) as Question
    const relativeReplies = patientQuestion.replies.filter(
      ({ value }) => value !== MAIN_PATIENT_ID && value !== INSERT_RELATIVE_PAGE,
    )
    patientQuestion.replies = [
      {
        imgUrl: cdn("/images/people_picto.svg"),
        title: `${languages.forMe} <br /> (${patient.firstname} ${patient.lastname})`,
        value: MAIN_PATIENT_ID,
        ui: { type: "primary" },
        preventionReportAllowed: canAccessToPreventionReport(age(patient.birthdate)),
      },
      ...relativeReplies,
      addRelativeReply,
    ]

    return create({ question: patientQuestion })
  }
  /*
   * Créer la question slot
   */
  const slots = (): Partial<SurveyStore> => {
    const slotsQuestion: Question = get(PATH.slot) as Question

    slotsQuestion.replies = getSlotsReplies(
      NUMBER_OF_SLOTS_BEFORE_NOW,
      NUMBER_OF_SLOTS_AFTER_NOW,
    )

    return create({ question: slotsQuestion })
  }

  /* Supprime une question
   * uniquement une question parent
   * par exemple pregnant , pour un patient courant HOMME
   * on supprimera la question pregnant
   */
  const remove = (pathToRemove: string) => {
    const questions = _questions.filter(
      ({ id }: { id: string }) => id !== pathToRemove,
    )

    const path = _path.filter((keyPath) => keyPath !== pathToRemove)

    return { ...state, questions, path }
  }
  const resetReplies = (): Partial<SurveyStore> => {
    const question: Question = get(PATH.selected_documents) as Question
    question.imgUrl = undefined
    question.replies = []
    return create({ question: question })
  }

  const addUploadedDocuments = (payload): Partial<SurveyStore> => {
    const SelectDocumentQuestion: Question = get(
      PATH.selected_documents,
    ) as Question || get(PATH.selected_documents_prevention) as Question
    const newReplies = payload.map(getUsefulData)
    SelectDocumentQuestion.imgUrl = newReplies.length
      ? undefined
      : cdn("images/document_send_illustration.svg")

    SelectDocumentQuestion.replies = newReplies
    return create({ question: SelectDocumentQuestion })
  }
  /* Création de question (ou remplacement d'une question existante)
   * Pour l'instant ne créé qu'une question parent ou sous question parent
   * Il remplace la question dans le store si jamais la question.id existait déjà
   * impossible d'ajouter par exemple patient:relative:old
   * on peut modifier patient:relative pour lui donner :old, par contre
   */
  const create = ({
    question,
    options,
  }: {
    question: Question
    options?: { before?: PATH; after?: PATH }
  }): Partial<SurveyStore> => {
    let questions: Question[] = _questions
    let pathUpdated: string[] = _path
    const exists = get(question.id)

    if (!exists && !is_deep_path(question.id)) {
      /*
       * Ici la question est une question parent et n'existe pas
       */
      if (options) {
        /*
         * Ici, nous avons précisé un emplacement pour la question.
         */
        const targetId = options.after || options.before
        const targetIndex = findIndex(questions, ({ id }) => id === targetId)

        if (targetIndex !== -1) {
          // Si on trouve la question cible, on insère à la bonne position
          const insertIndex = options.after ? targetIndex + 1 : targetIndex
          questions = [
            ...questions.slice(0, insertIndex),
            question,
            ...questions.slice(insertIndex),
          ]
        } else {
          // Si la question cible n'existe pas, on ajoute à la fin
          questions.push(question)
        }
      } else {
        // Pas d'options, on ajoute à la fin
        questions.push(question)
      }
    } else if (exists && !is_deep_path(question.id)) {
      /*
       * Ici la question est parent et existe. on replace
       */
      questions = replace_question(questions, question)
      // Le path n'est pas modifié car la question existe déjà
    } else if (is_deep_path(question.id)) {
      /*
       * Ici la questions sera ("patient:relative") par exemple
       */
      questions = questions.map((q) => {
        if (question.id.includes(q.id)) q.subquestion = question
        return q
      })
    }
    pathUpdated = addToPath(pathUpdated, question.id, options)
    return { ...state, questions, path: pathUpdated }
  }

  const finish = (): Partial<SurveyStore> => {
    return { ...state, status: "finish" }
  }
  const cancel = (): Partial<SurveyStore> => {
    return { ...state, status: "cancel" }
  }

  const _id: string = state.current?.id || "unknow-current"

  switch (action.type) {
    case ADD_DOCUMENTS_TO_QUESTION:
      return addUploadedDocuments(payload)
    case QUESTION_PATIENT:
      return patient({ patient: payload as Customer })
    case QUESTION_DOCTORS:
      return doctors({ doctors: payload as Doctor[] })
    case QUESTION_RELATIVES:
      return relatives({ relatives: payload as Relative[] })
    case QUESTION_SLOTS:
      return slots()
    case CREATE_QUESTION:
      return create(
        payload as {
          question: Question
          options?: { after?: PATH; before?: PATH }
        },
      )
    case UNSET_QUESTION: {
      // `actions.unset` sends the replies array itself, `unsetStepReplies`
      // sends `{ replies, id }`. Both must reach `unset` with the replies so
      // an un-selected subquestion reply also leaves the path.
      const unsetReplies = Array.isArray(payload)
        ? (payload as Reply[])
        : (payload as { replies?: Reply[] })?.replies
      const unsetId = Array.isArray(payload)
        ? _id
        : (payload as { id?: PATH })?.id ?? _id
      return {
        ...unset(unsetReplies, unsetId),
        prefill: markTouched(state.prefill, unsetId, unsetReplies),
      }
    }

    case SET_QUESTION: {
      const setReplies = (payload as any).replies
        ? ((payload as { replies?: Reply[] }).replies as Reply[])
        : (payload as Reply[])
      const setId = (payload as any).replies
        ? (payload as { id?: PATH }).id ?? _id
        : _id
      return {
        ...set(setReplies, setId),
        prefill: markTouched(state.prefill, setId, setReplies),
      }
    }
    case REMOVE_QUESTION:
      return remove(payload as string)
    case REMOVE_FROM_PATH:
      return { ...state, path: _path.filter((p) => p !== (payload as string)) }
    case NEXT_QUESTION:
      // validating a screen confirms its prefilled values
      return { ...next(), prefill: settleQuestion(state.prefill, _current.id) }
    case PREVIOUS_QUESTION: {
      // the answer of the screen being left is cleared, its prefill stays
      // consumed so it is never applied again; its unvalidated prefilled
      // values and the details screens they inserted are removed with it
      const previousState = withoutPendingDetails(
        previous(),
        _current.id === PATH.anamnesis_questions ? state.prefill : undefined,
      )
      return {
        ...previousState,
        answers: withoutPendingAnswers(
          previousState.answers || [],
          state.prefill,
          _current.id,
        ),
        prefill: settleQuestion(state.prefill, _current.id),
      }
    }
    case SET_DOCUMENT_NAVIGATION_LOADING:
      return { ...state, documentNavigationLoading: payload as boolean }
    case JUMP_TO_QUESTION: {
      const target = get(payload as string)
      return target ? { ...state, current: target } : state
    }
    case INIT_SURVEY:
      return init({ ...state } as Partial<SurveyStore>)
    case SET_CUSTOMER_TYPE:
      return customer(payload as CustomerType)
    case CONFIGURATION_SUCCESS:
      return configuration(payload as any)
    case REPLACE_QUESTIONS: {
      // Les questions sont remplacées, on mettre à jour le nouveau path
      // On conserve les réponses existantes
      const newQuestions = payload as Question[]
      const currentDoctorTypeQuestion = _questions.find(
        (q) => q.id === PATH.doctor_type,
      )
      const questionsWithPreservedReplies = currentDoctorTypeQuestion
        ? newQuestions.map((q) =>
            q.id === PATH.doctor_type
              ? { ...q, replies: currentDoctorTypeQuestion.replies }
              : q,
          )
        : newQuestions
      return {
        ...state,
        questions: questionsWithPreservedReplies,
        path: path(questionsWithPreservedReplies),
        current: first(questionsWithPreservedReplies),
      }
    }
    case RESET_SURVEY_STORE:
    case RESET_STORE:
      return reset({ ...state } as Partial<SurveyStore>)
    case RESET_SURVEY_REPLY:
      return {
        ...state,
        answers: _answers.filter(
          (answer) => answer.questionId !== action.payload,
        ),
      }
    case RESET_ANAMNESIS:
      return {
        ...withoutPendingDetails(state, state.prefill),
        answers: _answers.filter(
          (answer) => !ANAMNESIS_QUESTION_IDS.includes(answer.questionId),
        ),
        latestAnamnesis: undefined,
        prefill: undefined,
      }
    case SET_LATEST_ANAMNESIS:
      return { ...state, latestAnamnesis: payload as LatestAnamnesisCache }
    case PREFILL_ANAMNESIS:
      return {
        ...state,
        answers: mergePrefilledAnswer(_answers, payload as Answer),
        prefill: markPrefilled(state.prefill, payload as Answer),
      }
    case HYDRATE_SURVEY: {
      const snapshot = payload as SurveySnapshot
      const questions = snapshot.questions || []
      const findQuestion = (path_id: string): Question | undefined => {
        const complete_path = get_complete_path(path_id)
        const question = questions.find(({ id }) => complete_path.includes(id))
        if (complete_path.length === 1) return question
        return question ? recursive_get(question, complete_path) : undefined
      }
      const current =
        (snapshot.currentId && findQuestion(snapshot.currentId)) ||
        first(questions)
      // The documents question resets its answer on arrival and re-applies a
      // preselection: keep the restored selection aside so it survives that
      const restoredDocSelection: SurveyStore["restoredDocSelection"] = {}
      ;[PATH.selected_documents, PATH.selected_documents_prevention].forEach(
        (questionId) => {
          const answer = (snapshot.answers || []).find(
            (a) => a.questionId === questionId,
          )
          if (answer?.value?.length) {
            restoredDocSelection[questionId] = answer.value
          }
        },
      )
      return {
        ...state,
        questions,
        path: snapshot.path,
        answers: snapshot.answers || [],
        status: "on_going",
        current,
        restoredDocSelection,
        prefill: restorePrefillState(snapshot.prefillConsumed, snapshot.answers),
      }
    }
    case CLEAR_RESTORED_DOC_SELECTION:
      return {
        ...state,
        restoredDocSelection: undefined,
      }
    case SUBMIT_SURVEY_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case SUBMIT_SURVEY_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
      }

    case SUBMIT_SURVEY_ERROR:
      return {
        ...state,
        loading: false,
        success: false,
        message: action.payload as Message,
      }
    case RESET_SURVEY_QUESTION_REPLIES:
      return resetReplies()
    default:
      return state
  }
}
export default reducer
